import React, { useState, useEffect } from 'react';
import {
  KeyIcon,
  PencilIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

// API Base URL - detectar protocolo automaticamente (HTTPS quando site é HTTPS)
const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  const currentHost = window.location.hostname;
  const protocol = window.location.protocol;

  // Se for um domínio (contém ponto e não é IP), usar o mesmo domínio sem porta
  // O Nginx vai interceptar /api/* e rotear para o backend
  if (currentHost.includes('.') && !currentHost.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return `${protocol}//${currentHost}`;
  }

  // Se for localhost ou IP, usar porta 8000
  return `${protocol}//${currentHost}:8000`;
};

const API_BASE_URL = getApiBaseUrl();

// Interfaces
interface ApiConfiguration {
  id: number;
  api_type: string;
  api_type_display: string;
  api_name: string;
  api_category: string;
  api_category_display: string;
  description: string;
  api_key?: string;
  api_key_masked?: string;
  api_token?: string;
  api_token_masked?: string;
  base_url: string;
  is_active: boolean;
  requires_key: boolean;
  is_configured: boolean;
  last_test_date?: string;
  last_test_success: boolean;
  test_message?: string;
  created_at: string;
  updated_at: string;
}

interface ApisByCategory {
  [category: string]: ApiConfiguration[];
}

const ApiManagement: React.FC = () => {
  // State
  const [apis, setApis] = useState<ApiConfiguration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingApi, setEditingApi] = useState<ApiConfiguration | null>(null);
  const [editForm, setEditForm] = useState({
    api_key: '',
    api_token: '',
    base_url: '',
    is_active: true,
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);

  useEffect(() => {
    loadApis();
  }, []);

  // ==================== HELPER FUNCTIONS ====================

  const getAuthToken = (): string | null => {
    const authTokensStr = localStorage.getItem('authTokens');
    if (!authTokensStr) return null;

    try {
      const authTokens = JSON.parse(authTokensStr);
      return authTokens.access;
    } catch (err) {
      console.error('Erro ao parsear authTokens:', err);
      return null;
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons: { [key: string]: string } = {
      maps: '🗺️',
      data: '📊',
      calendar: '📅',
      signature: '✍️',
    };
    return icons[category] || '🔧';
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      maps: 'bg-blue-100 text-blue-700 border-blue-200',
      data: 'bg-green-100 text-green-700 border-green-200',
      calendar: 'bg-purple-100 text-purple-700 border-purple-200',
      signature: 'bg-orange-100 text-orange-700 border-orange-200',
    };
    return colors[category] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // ==================== API FUNCTIONS ====================

  const loadApis = async () => {
    try {
      setLoading(true);
      setError('');

      const token = getAuthToken();
      if (!token) {
        setError('Usuário não autenticado');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/settings/apis/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const apisList = Array.isArray(data) ? data : (data.results || []);
        setApis(apisList);
      } else {
        const errorData = await response.json();
        console.error('Erro na API:', errorData);
        setError(errorData.error || errorData.detail || 'Erro ao carregar APIs');
      }
    } catch (err) {
      console.error('Erro ao carregar APIs:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = async (api: ApiConfiguration) => {
    try {
      // Buscar dados completos da API (incluindo chaves não mascaradas)
      const token = getAuthToken();
      if (!token) {
        setError('Usuário não autenticado');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/settings/apis/${api.id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const fullApiData = await response.json();
        setEditingApi(fullApiData);
        setEditForm({
          api_key: fullApiData.api_key || '',
          api_token: fullApiData.api_token || '',
          base_url: fullApiData.base_url || '',
          is_active: fullApiData.is_active,
        });
        setShowApiKey(false);
        setShowApiToken(false);
        setShowEditModal(true);
      } else {
        setError('Erro ao carregar detalhes da API');
      }
    } catch (err) {
      console.error('Erro ao abrir modal:', err);
      setError('Erro ao carregar detalhes da API');
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingApi(null);
    setEditForm({
      api_key: '',
      api_token: '',
      base_url: '',
      is_active: true,
    });
    setShowApiKey(false);
    setShowApiToken(false);
  };

  const handleEditFormChange = (field: string, value: string | boolean) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveApi = async () => {
    if (!editingApi) return;

    try {
      setError('');
      setSuccess('');

      const token = getAuthToken();
      if (!token) {
        setError('Usuário não autenticado');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/settings/apis/${editingApi.id}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        await loadApis();
        closeEditModal();
        setSuccess('Configurações da API atualizadas com sucesso!');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Erro ao salvar configurações da API');
      }
    } catch (err) {
      console.error('Erro ao salvar API:', err);
      setError('Erro ao salvar configurações da API');
    }
  };

  // ==================== RENDER HELPERS ====================

  const groupApisByCategory = (): ApisByCategory => {
    const grouped: ApisByCategory = {};
    apis.forEach(api => {
      if (!grouped[api.api_category]) {
        grouped[api.api_category] = [];
      }
      grouped[api.api_category].push(api);
    });
    return grouped;
  };

  // ==================== RENDER ====================

  const apisByCategory = groupApisByCategory();

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow border">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-base font-bold text-gray-900">
            Configuração de APIs
          </h1>
          <p className="text-xs text-gray-600 mt-1">
            Gerencie as chaves e tokens das APIs externas utilizadas pelo sistema
          </p>
        </div>

        {/* Mensagens */}
        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="p-6 text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-xs text-gray-600 mt-2">Carregando APIs...</p>
          </div>
        ) : (
          <div className="p-6 space-y-8">
            {Object.keys(apisByCategory).length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                Nenhuma API cadastrada no sistema
              </p>
            ) : (
              Object.entries(apisByCategory).map(([category, categoryApis]) => (
                <div key={category}>
                  {/* Category Header */}
                  <div className="flex items-center mb-4">
                    <span className="text-2xl mr-2">{getCategoryIcon(category)}</span>
                    <h2 className="text-sm font-bold text-gray-900">
                      {categoryApis[0]?.api_category_display || category}
                    </h2>
                    <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {categoryApis.length} {categoryApis.length === 1 ? 'API' : 'APIs'}
                    </span>
                  </div>

                  {/* API Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {categoryApis.map(api => (
                      <div
                        key={api.id}
                        className={`border rounded-lg p-4 ${getCategoryColor(category)} transition-all hover:shadow-md`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-semibold text-gray-900">{api.api_name}</h3>
                              {api.is_configured ? (
                                <CheckCircleIcon
                                  className="h-5 w-5 text-green-600"
                                  title="Configurado"
                                />
                              ) : (
                                <ExclamationTriangleIcon
                                  className="h-5 w-5 text-yellow-600"
                                  title="Não configurado"
                                />
                              )}
                            </div>

                            <p className="text-xs text-gray-600 mb-3">
                              {api.description}
                            </p>

                            <div className="space-y-1">
                              {api.requires_key && (
                                <div className="flex items-center text-xs text-gray-500">
                                  <KeyIcon className="h-4 w-4 mr-1" />
                                  <span>
                                    {api.api_key_masked ? (
                                      <span className="font-mono">{api.api_key_masked}</span>
                                    ) : (
                                      <span className="text-yellow-600">Chave não configurada</span>
                                    )}
                                  </span>
                                </div>
                              )}

                              {api.base_url && (
                                <div className="text-xs text-gray-500 truncate" title={api.base_url}>
                                  🌐 {api.base_url}
                                </div>
                              )}

                              <div className="flex items-center space-x-2 mt-2">
                                {api.is_active ? (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                    Ativa
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                                    Inativa
                                  </span>
                                )}

                                {!api.requires_key && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                    Gratuita
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={() => openEditModal(api)}
                            className="ml-3 p-2 text-gray-600 hover:text-gray-900 hover:bg-white/50 rounded transition-colors"
                            title="Editar configurações"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingApi && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-900">
                Editar {editingApi.api_name}
              </h2>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Descrição */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-600">{editingApi.description}</p>
              </div>

              {/* URL Base */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL Base
                </label>
                <input
                  type="text"
                  value={editForm.base_url}
                  onChange={(e) => handleEditFormChange('base_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://api.example.com"
                />
              </div>

              {/* API Key */}
              {editingApi.requires_key && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Key / Chave
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={editForm.api_key}
                      onChange={(e) => handleEditFormChange('api_key', e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="Insira a chave da API"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showApiKey ? (
                        <EyeSlashIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* API Token */}
              {editingApi.requires_key && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Token (opcional)
                  </label>
                  <div className="relative">
                    <input
                      type={showApiToken ? 'text' : 'password'}
                      value={editForm.api_token}
                      onChange={(e) => handleEditFormChange('api_token', e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="Insira o token (se aplicável)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiToken(!showApiToken)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showApiToken ? (
                        <EyeSlashIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Status Ativo */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={(e) => handleEditFormChange('is_active', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">API Ativa</span>
                </label>
                <p className="ml-6 text-xs text-gray-500 mt-1">
                  Desmarque para desativar temporariamente esta API no sistema
                </p>
              </div>

              {/* Informações adicionais */}
              {editingApi.last_test_date && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600">
                    <strong>Último teste:</strong>{' '}
                    {new Date(editingApi.last_test_date).toLocaleString('pt-BR')}
                  </p>
                  {editingApi.test_message && (
                    <p className="text-xs text-gray-600 mt-1">
                      <strong>Resultado:</strong> {editingApi.test_message}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
              <button
                onClick={closeEditModal}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveApi}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Salvar Configurações
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiManagement;
